import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requireFeature } from '@/common/utils/requireFeature';
import { techSystemsService } from '@/features/tech-systems/services/tech-systems.service';
import { Permission } from '@/common/enums/permissions.enum';
import { PermissionGate } from '@/components/PermissionGate';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Archive, Trash2, UserPlus } from 'lucide-react';
import { AddMemberDialog } from '@/features/tech-systems/components/AddMemberDialog';
import { TechSystemDocsSection } from '@/features/tech-systems/components/TechSystemDocsSection';
import { TechSystemMindmapSection } from '@/features/tech-systems/components/TechSystemMindmapSection';
import { TechSystemMonitoringSection } from '@/features/tech-systems/components/TechSystemMonitoringSection';
import { TechSystemPasswordsSection } from '@/features/tech-systems/components/TechSystemPasswordsSection';
import { TechSystemTicketsTab } from '@/features/tech-systems/components/TechSystemTicketsTab';
import { TechSystemOverviewSection } from '@/features/tech-systems/components/TechSystemOverviewSection';
import { TechSystemCodeSection } from '@/features/tech-systems/components/TechSystemCodeSection';
import { TechSystemBackupsSection } from '@/features/tech-systems/components/TechSystemBackupsSection';
import { TechSystemProjectsTab } from '@/features/tech-systems/components/TechSystemProjectsTab';
import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  labelMemberLevel,
  TECH_SYSTEM_STATUS_LABELS,
} from '@/features/tech-systems/constants/labels';

export const Route = createFileRoute('/_authenticated/tech-systems/$id')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemDetailPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: (s.tab as string) || 'overview',
  }),
});

export function TechSystemMembersList({
  members,
  canEdit,
  onRemove,
}: {
  members: { id: string; userId: string; userEmail?: string; accessLevel: string }[];
  canEdit: boolean;
  onRemove: (userId: string) => void;
}) {
  const confirm = useConfirm();
  return (
    <ul className="space-y-2 text-sm">
      {members.map((m) => (
        <li
          key={m.id}
          className="flex items-center justify-between rounded border px-3 py-2"
        >
          <span>
            {m.userEmail} — <strong>{labelMemberLevel(m.accessLevel as never)}</strong>
          </span>
          {canEdit && (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Remover membro ${m.userEmail}`}
              onClick={async () => {
                if (
                  !(await confirm({
                    title: `Excluir membro “${m.userEmail}”?`,
                    description: 'O membro perde o acesso a este sistema.',
                    confirmLabel: 'Excluir membro',
                    destructive: true,
                  }))
                )
                  return;
                onRemove(m.userId);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

function TechSystemDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission, user } = usePermissions();
  const canManageGlobal = hasPermission(Permission.TECH_SYSTEMS_MANAGE);
  const canConfig = hasPermission(Permission.TECH_SYSTEMS_CONFIG);

  const { data: system, isLoading } = useQuery({
    queryKey: ['tech-system', id],
    queryFn: () => techSystemsService.get(id),
  });

  const memberLevel = system?.members?.find((m) => m.userId === user?.id)
    ?.accessLevel;
  const canEdit =
    canManageGlobal || canConfig || memberLevel === 'manager';
  const [status, setStatus] = useState('active');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);

  useEffect(() => {
    if (system?.status) {
      setStatus(system.status);
    }
  }, [system?.status]);

  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: string) =>
      techSystemsService.update(id, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-system', id] });
      queryClient.invalidateQueries({ queryKey: ['tech-systems'] });
      toast({ title: 'Status atualizado' });
    },
    onError: () =>
      toast({ title: 'Erro ao atualizar status', variant: 'destructive' }),
  });

  const archiveSystemMutation = useMutation({
    mutationFn: () => techSystemsService.update(id, { status: 'archived' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-systems'] });
      toast({ title: 'Sistema arquivado' });
      navigate({ to: '/tech-systems' });
    },
    onError: () =>
      toast({ title: 'Erro ao arquivar sistema', variant: 'destructive' }),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => techSystemsService.removeMember(id, userId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['tech-system', id] }),
  });

  if (isLoading || !system) {
    return (
      <PermissionGate permission={Permission.TECH_SYSTEMS_VIEW}>
        <div className="p-6">Carregando...</div>
      </PermissionGate>
    );
  }

  return (
    <PermissionGate permission={Permission.TECH_SYSTEMS_VIEW}>
    <div className="space-y-4 p-6">
      <Link to="/tech-systems" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 h-4 w-4" />
        Voltar
      </Link>
      <h1 className="text-2xl font-semibold">{system.name}</h1>

      <Tabs defaultValue={tab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" asChild>
            <Link to="." search={{ tab: 'overview' }} replace>
              Visão geral
            </Link>
          </TabsTrigger>
          <TabsTrigger value="code" asChild>
            <Link to="." search={{ tab: 'code' }} replace>
              Código
            </Link>
          </TabsTrigger>
          <TabsTrigger value="tickets" asChild>
            <Link to="." search={{ tab: 'tickets' }} replace>
              Tickets
            </Link>
          </TabsTrigger>
          <TabsTrigger value="projects" asChild>
            <Link to="." search={{ tab: 'projects' }} replace>
              Projetos
            </Link>
          </TabsTrigger>
          <TabsTrigger value="backups" asChild>
            <Link to="." search={{ tab: 'backups' }} replace>
              Backups
            </Link>
          </TabsTrigger>
          <TabsTrigger value="passwords" asChild>
            <Link to="." search={{ tab: 'passwords' }} replace>
              Senhas
            </Link>
          </TabsTrigger>
          <TabsTrigger value="docs" asChild>
            <Link to="." search={{ tab: 'docs' }} replace>
              Documentação
            </Link>
          </TabsTrigger>
          <TabsTrigger value="monitoring" asChild>
            <Link to="." search={{ tab: 'monitoring' }} replace>
              Monitoramento
            </Link>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <TechSystemOverviewSection
            techSystemId={id}
            system={system}
            canEdit={canEdit}
          />

          {canEdit && (
            <Card>
              <CardHeader>
                <CardTitle>Administração</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="space-y-3 rounded-md border p-4 pt-3">
                  <p className="font-medium">Alterar status</p>
                  <p className="text-xs text-muted-foreground">
                    Use <strong>Arquivado</strong> ou <strong>Descontinuado</strong>{' '}
                    para tirar o sistema da operação sem apagá-lo. Na lista de
                    gestão, por padrão só aparecem os ativos — use o filtro
                    &quot;Exibir&quot; para ver arquivados ou descontinuados.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[200px] flex-1 space-y-1">
                      <Label>Status do sistema</Label>
                      <Select
                        value={status}
                        onValueChange={(value) => {
                          setStatus(value);
                          updateStatusMutation.mutate(value);
                        }}
                        disabled={updateStatusMutation.isPending}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(TECH_SYSTEM_STATUS_LABELS).map(
                            ([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

              {system.status === 'active' && (
                <div className="rounded-md border p-4 space-y-2">
                  <p className="font-medium">Arquivar sistema</p>
                  <p className="text-xs text-muted-foreground">
                    O sistema deixa de aparecer na lista padrão (somente ativos),
                    mas permanece no histórico e pode ser reativado alterando o
                    status.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setArchiveDialogOpen(true)}
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Arquivar sistema
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Arquivar sistema?</DialogTitle>
                <DialogDescription>
                  O sistema &quot;{system.name}&quot; será marcado como arquivado e
                  deixará de aparecer na listagem padrão. Você pode reativá-lo
                  depois alterando o status.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setArchiveDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  disabled={archiveSystemMutation.isPending}
                  onClick={() => archiveSystemMutation.mutate()}
                >
                  Confirmar arquivamento
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Usuários com acesso</CardTitle>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAddMemberDialogOpen(true)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Adicionar usuário
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <TechSystemMembersList
                members={system.members ?? []}
                canEdit={canEdit}
                onRemove={(userId) => removeMemberMutation.mutate(userId)}
              />
            </CardContent>
          </Card>

          <AddMemberDialog
            techSystemId={id}
            open={addMemberDialogOpen}
            onOpenChange={setAddMemberDialogOpen}
            existingMemberUserIds={(system.members ?? []).map((m) => m.userId)}
          />
        </TabsContent>

        <TabsContent value="code">
          <TechSystemCodeSection techSystemId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="tickets">
          <TechSystemTicketsTab techSystemId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="projects">
          <TechSystemProjectsTab techSystemId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="backups">
          <TechSystemBackupsSection techSystemId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="passwords">
          <TechSystemPasswordsSection techSystemId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="docs" className="space-y-4">
          <TechSystemDocsSection techSystemId={id} canEdit={canEdit} />
          <TechSystemMindmapSection techSystemId={id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="monitoring">
          <TechSystemMonitoringSection techSystemId={id} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
    </PermissionGate>
  );
}
